import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  CreditCard,
  KeyRound,
  Lock,
  PenTool,
  Plug,
  Receipt as ReceiptIcon,
  Send,
  Sparkles,
  User,
  UserCog,
  Webhook,
} from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import ProfileSection from './ProfileSection';
import SecuritySection from './SecuritySection';
import AIKeysSection from './AIKeysSection';
import PromptsSection from './PromptsSection';
import IntegrationsSection from './IntegrationsSection';
import WebhooksSection from './WebhooksSection';
import NotificationsSection from './NotificationsSection';
import EmailSenderSection from './EmailSenderSection';
import BillingSection from './BillingSection';

// Section list — order = display order in the rail.
const SECTIONS = [
  { to: 'profile', label: 'Profile', icon: User, element: <ProfileSection /> },
  { to: 'security', label: 'Security', icon: Lock, element: <SecuritySection /> },
  { to: 'ai-keys', label: 'AI providers', icon: KeyRound, element: <AIKeysSection /> },
  { to: 'prompts', label: 'Custom prompts', icon: PenTool, element: <PromptsSection /> },
  { to: 'integrations', label: 'Integrations', icon: Plug, element: <IntegrationsSection /> },
  { to: 'webhooks', label: 'Webhooks', icon: Webhook, element: <WebhooksSection /> },
  { to: 'notifications', label: 'Notifications', icon: Bell, element: <NotificationsSection /> },
  { to: 'email-sender', label: 'Email sender', icon: Send, element: <EmailSenderSection /> },
  { to: 'billing', label: 'Billing', icon: CreditCard, element: <BillingSection /> },
];

// Layout container — left rail on md+, top dropdown on smaller screens.
// Routes mounted under /account/* so direct links to a section work
// (/account/billing, /account/ai-keys, etc.).
export default function AccountLayout() {
  return (
    <div className="space-y-5">
      <PageHeader
        variant="gradient"
        icon={UserCog}
        eyebrow="Account"
        title="Account settings"
        description="Profile, security, API keys, integrations, notifications, and billing — all in one place."
      />

      {/* Mobile section picker */}
      <div className="md:hidden">
        <MobileSectionPicker />
      </div>

      {/* md+ split layout */}
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5 items-start">
        <SectionRail />
        <div className="min-w-0">
          <Routes>
            <Route index element={<Navigate to="profile" replace />} />
            {SECTIONS.map((s) => (
              <Route key={s.to} path={s.to} element={s.element} />
            ))}
            <Route path="*" element={<Navigate to="profile" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function SectionRail() {
  return (
    <aside className="hidden md:block sticky top-4">
      <nav className="card !p-2">
        <ul className="space-y-0.5">
          {SECTIONS.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-brand-50 text-brand-700 font-medium'
                      : 'text-ink-600 hover:bg-gray-50'
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

function MobileSectionPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const current =
    SECTIONS.find((s) => location.pathname.endsWith(`/account/${s.to}`))?.to || 'profile';
  return (
    <div className="card !p-2">
      <select
        className="input"
        onChange={(e) => navigate(`/account/${e.target.value}`)}
        value={current}
      >
        {SECTIONS.map(({ to, label }) => (
          <option key={to} value={to}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
}
